import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import Modal from '../../../../../UIelements/Modal';
import * as Styled from '../styles';
import { authenticationPassword, editAccount } from '../../../../../../services';
import { toast } from 'react-toastify';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../../../../../db/db';
import Loading from '../../../../../Loading';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { Button, Input } from '@praticabr/ppsa';
import { zodResolver } from '@hookform/resolvers/zod';

type ManualError = {
    type: "manual";
    message: string;
};

const createError = (message: string): ManualError => ({
    type: "manual",
    message
});

type ModalProps = {
    user: any;
    onCancel: () => void;
    onChangePage: (page: string) => void;
}

const changePasswordFormSchema = z
    .object({
        currentPassword: z.string().trim().nonempty('user.Preencha a senha atual'),
        password: z.string().trim().nonempty('user.Preencha a nova senha'),
        passwordConfirm: z
            .string()
            .trim()
            .nonempty('user.Preenha a confirmação da nova senha'),
    })
    .superRefine((data, ctx) => {
        if (data.password !== data.passwordConfirm) {
            ctx.addIssue({
                code: "custom",
                path: ['passwordConfirm'],
                message: 'main.As senhas devem ser iguais',
            });
        }
    });


type FormDataChangePassword = z.infer<typeof changePasswordFormSchema>;

export const ModalChangePassword: React.FC<ModalProps> = (props) => {
    const intl = useIntl();
    const userDb = useLiveQuery(() => db.user.toArray());
    const [isLoading, setIsLoading] = useState(false);

    const { register, handleSubmit, formState: { errors } } = useForm<FormDataChangePassword>({
        resolver: zodResolver(changePasswordFormSchema),
    })


    const submitChangePassword = (data: FormDataChangePassword) => {
        try {
            const submit = {
                user: {
                    id: userDb![0].id,
                    userName: props.user.userName,
                    email: props.user.email,
                    emailVerified: userDb![0].emailVerified,
                    password: data.password,
                    companyId: userDb![0].companyId,
                    phone: props.user.phone,
                    userTypeId: userDb![0].userTypeId
                }
            }
            const userInfo = {
                email: props.user.email,
                password: data.currentPassword
            }
            if (userDb) {
                authenticationPassword(userInfo, userDb[0].accessToken)
                    .then(async (_) => {
                        try {
                            const editedAccount = await editAccount(submit, userDb[0].accessToken)
                            await new Promise<void>((resolve) => setTimeout(function () {
                                toast.success(intl.formatMessage({ id: 'main.Conta editada com sucesso!' }).toUpperCase());
                                db.user.update(props.user.id, editedAccount.data.editedUserData);
                                props.onCancel();
                                resolve()
                            }, 250))
                        } catch (error) {
                            console.error(error);
                            toast.error(intl.formatMessage({ id: 'menus.Existem campos com informações inválidas' }).toUpperCase());
                        }
                    })
                    .catch(error => {
                        console.error(error);
                        toast.error(intl.formatMessage({ id: 'main.Informe sua senha atual corretamente' }).toUpperCase());
                    }).finally(() => {
                        setIsLoading(false)
                    })
            }
        } catch (error) {
            toast.error(intl.formatMessage({ id: 'menus.Existem campos com informações inválidas' }).toUpperCase());
        }
    };


    const footerChangePassword = (
        <>
            <Button type='button' size='lg' variant='confirmation-modal' width='108px' onClick={handleSubmit(submitChangePassword)} >
                {intl.formatMessage({ id: 'main.SALVAR' })}
            </Button>
            <Button type='button' size='lg' variant='cancellation' width='108px' onClick={() => props.onChangePage("EditAccount")} >
                {intl.formatMessage({ id: 'menus.CANCELAR' })}
            </Button>
        </>
    );


    return (
        <Modal
            title={intl.formatMessage({ id: 'main.ALTERAR SENHA' })}
            onCancel={props.onCancel}
            footer={footerChangePassword}
            width='380px'
        >
            {isLoading && <Loading />}
            {!isLoading && (
                <>
                    <Styled.DivText className='first'>
                        <Input
                            label={intl.formatMessage({ id: 'main.Senha atual' }) + ' *'}
                            id='currentPassword'
                            type='password'
                            placeholder={intl.formatMessage({ id: 'main.Informe sua senha atual' })}
                            {...register('currentPassword')}
                        />
                    </Styled.DivText>
                    <Styled.DivText>
                        <Input
                            label={intl.formatMessage({ id: 'main.Nova senha' }) + ' *'}
                            id='password'
                            type='password'
                            placeholder={intl.formatMessage({ id: 'main.Informe sua nova senha' })}
                            {...register('password')}
                            errors={errors.password && createError(intl.formatMessage({ id: 'register.Sua senha deve estar entre 8 e 20 caracteres, ter uma letra maiúscula, uma minúscula, um número e um caractere especial' }))}
                        />
                    </Styled.DivText>

                    <Styled.DivText className='last'>
                        <Input
                            label={intl.formatMessage({ id: 'main.Confirmar nova senha' }) + ' *'}
                            id='passwordConfirm'
                            type='password'
                            placeholder={intl.formatMessage({ id: 'main.Confirme sua nova senha' })}
                            {...register('passwordConfirm')}
                            errors={errors.passwordConfirm && createError(intl.formatMessage({ id: 'register.Sua senha deve estar entre 8 e 20 caracteres, ter uma letra maiúscula, uma minúscula, um número e um caractere especial' }))}
                        />
                        <p>{intl.formatMessage({ id: 'register.Sua senha deve estar entre 8 e 20 caracteres, ter uma letra maiúscula, uma minúscula, um número e um caractere especial' })}</p>
                    </Styled.DivText>
                </>
            )}
        </Modal>
    )
}